/**
 * useMainKeyboard.ts
 */
import Direction from '@shared/types/enums/direction';
import Event from '@shared/classes/event';
import Focus from '@shared/types/enums/focus';
import Main from '@shared/types/enums/main';
import MainProps from '@shared/types/props/main';
import Pages from '@pages';
import Rail from '@shared/classes/rail';
import Routes from '@shared/types/enums/routes';
import Slider from '@shared/classes/slider';
import useKeyboard from '@shared/components/utils/hooks/useKeyboard';
import { Coordinates } from '@shared/utils/floatingMenu';
import { MainTimeline } from '@shared/types/interfaces/main';
import { MutableRefObject, useCallback, useRef } from 'react';
import { openFloatingMenu } from '@shared/store/thunks/floatingMenu.thunk';
import { popRoute } from '@shared/store/slices/router.slice';
import { setFocus } from '@shared/store/slices/focus.slice';
import { store } from '@shared/store/store';
import { useAppDispatch } from '@shared/store/hooks';
import {
  HORIZONTAL_KEY_DELAY,
  VERTICAL_KEY_DELAY,
} from '@shared/types/constants/main';

interface MainKeyboardProps {
  channelsRef: MutableRefObject<Rail | undefined>;
  filterRef: MutableRefObject<Rail | undefined>;
  flagsRef: MutableRefObject<MainProps['flags']>;
  railsRef: MutableRefObject<Rail[] | undefined>;
  searchRef: MutableRefObject<boolean>;
  slidersRef: MutableRefObject<{ id: string; data: Slider[] | undefined }>;
  timelineRef: MutableRefObject<MainTimeline | null>;
  cancelScheduledParamsWrite: () => void;
  changeAsset: (id: string, index: number) => void;
  focusSelection: (id: string) => void;
  onPagination?: (type: Main, direction: Direction, rail?: Rail | null) => void;
  resetLayout: () => void;
  scheduleParamsWrite: () => void;
  selectAsset: (id: string, index: number) => void;
}

/**
 * Floating menu coordinate mappings for different rail display modes.
 * Determines where to position the floating menu based on rail type.
 */
const FLOATING_MENU_COORDINATES = {
  photo: Coordinates.MAIN,
  channelGuidePhoto: Coordinates.MAIN,
  character: Coordinates.MAIN,
  epg: Coordinates.EPG,
  poster: Coordinates.POSTER_RAIL,
  progressPhoto: Coordinates.MAIN,
  square: Coordinates.SQUARE_RAIL,
  related: Coordinates.RELATED_RAIL,
};

/**
 * List of focus that can be focused by the content navigation.
 */
const FOCUS_LIST = [
  Focus.CHANNELS_RAIL,
  Focus.FILTER_RAIL,
  Focus.RAILS_LIST,
  Focus.SEARCH_FILTER,
  Focus.SLIDERS,
];

/**
 * Wires up the Main hook's keyboard-driven navigation (content/rail
 * navigation, back/select, the floating menu long-press, menu re-entry).
 * Registers its own `useKeyboard` listeners; nothing here is returned or
 * used outside this hook.
 */
export default function useMainKeyboard({
  channelsRef,
  filterRef,
  flagsRef,
  railsRef,
  searchRef,
  slidersRef,
  timelineRef,
  cancelScheduledParamsWrite,
  changeAsset,
  focusSelection,
  onPagination,
  resetLayout,
  scheduleParamsWrite,
  selectAsset,
}: MainKeyboardProps) {
  const dispatch = useAppDispatch();

  // Trigger limits
  const horizontalTriggerLimitRef = useRef<Date | null>(null); // For horizontal navigation
  const verticalTriggerLimitRef = useRef<Date | null>(null); // For vertical navigation

  /**
   * Handles the bounds action for the content navigation.
   * @param key - The key pressed.
   * @param id - The id of the element the navigation is bound to.
   */
  const boundsAction = useCallback(
    (key: string, id: string, isEnd?: boolean) => {
      switch (key) {
        case 'LEFT':
          // If focusing filter and search is rendered, focus search
          if (id === filterRef.current?.id && searchRef.current) {
            dispatch(setFocus(Focus.SEARCH_FILTER));
            return;
          }
          // If on a Search based page, focus keyboard
          if (store.getState().router.route.Page === Pages.SEARCH) {
            dispatch(setFocus(Focus.SEARCH_KEYBOARD));
            return;
          }
          // EPG timeline bounds, fetch next chunk
          if (
            flagsRef.current?.timeline &&
            timelineRef.current &&
            id !== filterRef.current?.id
          ) {
            const handled = timelineRef.current.boundsAction(key);
            if (handled) {
              resetLayout();
              return;
            }
          }
          // Else, focus menu
          dispatch(setFocus(Focus.MENU));
          break;
        case 'RIGHT':
          // EPG timeline bounds (only if last asset is focused)
          if (
            flagsRef.current?.timeline &&
            timelineRef.current &&
            id !== filterRef.current?.id &&
            isEnd
          ) {
            const handled = timelineRef.current.boundsAction(key);
            if (handled) resetLayout();
          }
          // Else, paginate
          else {
            const railHorizontal = railsRef.current?.find((el) => el.id === id);
            railHorizontal &&
              onPagination?.(Main.RAILS, Direction.HORIZONTAL, railHorizontal);
          }
          break;
        case 'DOWN':
          const railVertical = railsRef.current?.find((el) => el.id === id);
          railVertical &&
            onPagination?.(Main.RAILS, Direction.VERTICAL, railVertical);
          break;
      }
    },
    []
  );

  /**
   * Handles the content navigation (slider is managed by the sliders component).
   * @param key - The key pressed.
   */
  const contentKeyDownHandler = useCallback(
    (key: string, isRepeat?: boolean) => {
      // Block keys if main is not complete or is loading
      if (flagsRef.current?.loading) return;

      // If the key is not allowed to trigger, return
      const canHorizontalTrigger =
        !isRepeat ||
        Date.now() - (horizontalTriggerLimitRef.current?.getTime() || 0) >=
          HORIZONTAL_KEY_DELAY;
      const canVerticalTrigger =
        !isRepeat ||
        Date.now() - (verticalTriggerLimitRef.current?.getTime() || 0) >=
          VERTICAL_KEY_DELAY;
      if (canHorizontalTrigger) horizontalTriggerLimitRef.current = new Date();
      if (canVerticalTrigger) verticalTriggerLimitRef.current = new Date();

      // Check if a special component is focused
      const { focus: focusSlice } = store.getState();
      const isSearchFocused = focusSlice.focus === Focus.SEARCH_FILTER;

      // Handle the key
      switch (key) {
        // Vertical navigation
        case 'DOWN': {
          if (!canVerticalTrigger) return;
          const { layout: currentLayout, selection: currentSelection } =
            store.getState().main;
          const newSelection =
            currentLayout[currentSelection]?.pointer?.next || currentSelection;
          const nextPointer = currentLayout[newSelection]?.pointer?.next;
          if (!nextPointer) boundsAction(key, currentSelection);
          if (newSelection !== currentSelection) focusSelection(newSelection);
          break;
        }
        case 'UP': {
          if (!canVerticalTrigger) return;
          const { layout: currentLayout, selection: currentSelection } =
            store.getState().main;
          const newSelection =
            currentLayout[currentSelection]?.pointer?.previous ||
            currentSelection;
          if (newSelection !== currentSelection) focusSelection(newSelection);
          break;
        }
        // Horizontal navigation
        case 'LEFT': {
          if (!canHorizontalTrigger) return;
          const { layout: currentLayout, selection: currentSelection } =
            store.getState().main;
          if (currentSelection === slidersRef.current?.id) return;
          if (isSearchFocused) {
            dispatch(setFocus(Focus.MENU));
            return;
          }
          if (!currentSelection) return;
          const oldIndex = currentLayout[currentSelection]?.hIndex || 0;
          const newIndex = Math.max(0, oldIndex - 1);
          if (newIndex === oldIndex) {
            boundsAction(key, currentSelection);
          } else {
            changeAsset(currentSelection, newIndex);
          }
          break;
        }
        case 'RIGHT': {
          if (!canHorizontalTrigger) return;
          const { layout: currentLayout, selection: currentSelection } =
            store.getState().main;
          if (currentSelection === slidersRef.current?.id) return;
          if (isSearchFocused) {
            dispatch(setFocus(Focus.FILTER_RAIL));
            return;
          }
          if (!currentSelection) return;
          const layoutConfig = currentLayout[currentSelection];
          const oldIndex = layoutConfig?.hIndex || 0;
          const newIndex = Math.min(layoutConfig?.length - 1, oldIndex + 1);
          if (newIndex === oldIndex || newIndex >= layoutConfig?.length - 1) {
            boundsAction(key, currentSelection, newIndex === oldIndex);
            if (newIndex === oldIndex) {
              const rail = railsRef.current?.find(
                (el) => el.id === currentSelection
              );
              // --- If the rail is circular, change the asset to the first one (special case for circular rails on Right key)
              if (rail?.circular) {
                changeAsset(currentSelection, 0);
              }
              // --- End of special case for circular rails. Else, do nothing further.
              break;
            }
          }
          changeAsset(currentSelection, newIndex);
          break;
        }
      }
    },
    [dispatch]
  );

  /**
   * Handles the key up events for the content navigation.
   * @param key - The key pressed.
   */
  const contentKeyUpHandler = useCallback((key: string) => {
    switch (key) {
      case 'BACK': {
        const { selection: currentSelection } = store.getState().main;
        if (
          flagsRef.current?.timeline &&
          filterRef.current &&
          currentSelection !== filterRef.current?.id
        ) {
          focusSelection(filterRef.current?.id ?? currentSelection);
          return;
        }
        const { router } = store.getState();
        if (router.route.stack.length) {
          // Cancel pending write before popRoute() can clobber it.
          cancelScheduledParamsWrite();
          dispatch(popRoute());
          return;
        }
        dispatch(setFocus(Focus.MENU));
        break;
      }
      case 'SELECT': {
        const { layout: currentLayout, selection: currentSelection } =
          store.getState().main;
        selectAsset(
          currentSelection,
          currentLayout[currentSelection]?.hIndex || 0
        );
        break;
      }
    }
  }, []);

  const contentKeyPressHandler = useCallback(() => {
    const { asset: currentAsset, selection: currentSelection } =
      store.getState().main;
    const rail = [channelsRef.current, ...(railsRef.current || [])]
      ?.filter((el) => !!el)
      ?.find((el) => el.id === currentSelection);
    if (!rail) return;
    const { router } = store.getState();
    scheduleParamsWrite();
    dispatch(
      openFloatingMenu({
        route: Routes.MAIN,
        asset: currentAsset as Event,
        position:
          rail?.displayMode === 'photo' && router.route.Page === Pages.DETAIL
            ? FLOATING_MENU_COORDINATES['related']
            : FLOATING_MENU_COORDINATES[
                rail.displayMode as keyof typeof FLOATING_MENU_COORDINATES
              ],
        type: rail?.type,
      })
    );
  }, []);

  /**
   * Handles the key down events for the menu navigation.
   * @param key - The key pressed.
   */
  const menuKeyDownHandler = useCallback((key: string) => {
    switch (key) {
      case 'RIGHT':
        focusSelection(store.getState().main.selection);
        break;
    }
  }, []);

  /**
   * Handles the content navigation.
   * @param key - The key pressed.
   */
  useKeyboard({
    focus: FOCUS_LIST,
    keyDownHandler: contentKeyDownHandler,
    keyUpHandler: contentKeyUpHandler,
    keyPressHandler: contentKeyPressHandler,
  });

  /**
   * Handles the menu navigation.
   * @param key - The key pressed.
   */
  useKeyboard({
    focus: Focus.MENU,
    keyDownHandler: menuKeyDownHandler,
  });
}
